/**
 * W1-11b (app-shell-ui): slot arrangement per workspace and layout preset (ARCHITECTURE.md §6.2; DESIGN.md §3.2,
 * §3.3, §4.5, §4.15, §4.16).
 *
 *  Chart:   tools rail | timeline | preview over dock | inspector      (Compose / Animate / Review)
 *           Laptop, Full:   rail | timeline, floating mini preview (F8), right drawer with dock + inspector (F9)
 *           Laptop, Guided: rail | timeline | preview (>= 35 %, also when dragged), drawer with dock + inspector (F9)
 *           Narrow tablet (< 900 px): icon rail | timeline | preview, drawer with dock + inspector (F9)
 *  Timing:  timing.left | timing.main over timing.bottom
 *  Publish: publish.left | publish.right
 *
 * Proportions come from LayoutPresets.ts for the window size, preset and level; dividers are draggable (and keyboard
 * operable) and the sizes the user sets are remembered per workspace, preset and level (settings `ext`, saved after
 * the drag ends). `ui.showPanel` / `ui.focusRegion` open the drawer or the mini preview when the slot lives there.
 *
 * Measurements (W3-01, UX-001 / UX-003 / UX-019): the workspace's real height (banners take some of it) and the
 * preview panel's chrome (toolbar rows and readout, which wrap with the width) feed the Chart layout, so the docked
 * stage gets exactly its 16:9 height. The floating mini preview is anchored to the bottom-right corner, sized from
 * the same measurement, and kept fully inside the window whenever the window or the layout changes.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import type { Json } from '../../core/util/types';
import type { LayoutPreset, WorkspaceId } from '../../state/types';
import { IconButton } from '../../ui/IconButton';
import { Splitter } from '../../ui/Splitter';
import type { AppServices, PanelSlot } from '../registry/types';
import type { ShellUiService, UiSnapshot } from '../uiService';
import {
  BREAKPOINTS,
  chartSplitSizes,
  CHROME,
  computeChartLayout,
  enforcePaneMin,
  computePublishLayout,
  computeTimingLayout,
  effectiveLayout,
  estimatePreviewChrome,
  LAYOUT_PRESETS,
  layoutSizesKey,
  previewMinPct,
  sanitizeSizes,
  type ChartLayout,
} from './LayoutPresets';
import { problemsPanelId } from './shellCommands';
import { SlotHost } from './SlotHost';
import {
  useLayoutViewport,
  useOptionalShell,
  useSettingsValue,
  useText,
  useUi,
  useWindowSize,
} from './ShellContext';
import { m } from './shell.messages';
import l from './Layout.module.css';

/** settings ext key: user-resized splitter sizes, by layoutSizesKey + part. */
export const SIZES_EXT = 'shell.layoutSizes';
/** settings ext key: mini preview position. */
export const MINI_EXT = 'shell.miniPreview';
const SAVE_DELAY_MS = 300;

/**
 * The motion bottom drawer's slot (W3-01, CR-W2-04a-7): the `chart.motionDrawer` slot under the lane below
 * 1024 px. Listed in `PanelSlot` since CR-W3-01-1 (Wave 3 gate), so the type is direct — a module fills it with
 * `slot: MOTION_DRAWER_SLOT`.
 */
export const MOTION_DRAWER_SLOT: PanelSlot = 'chart.motionDrawer';

/** Keeps splitter sizes within a layout rule: `(next, prev)` -> allowed sizes (`prev` = before the move). */
type SizeRule = (next: readonly number[], prev: readonly number[]) => readonly number[];

/**
 * Sizes of one splitter: the remembered ones when they fit, else the computed defaults (live while dragging). A
 * `rule` (Guided: the preview keeps 35 %) applies to drags, keys and remembered sizes alike.
 */
function useSplitSizes(
  services: AppServices,
  key: string,
  defaults: readonly number[],
  rule?: SizeRule,
): readonly [readonly number[], (next: readonly number[]) => void] {
  const stored = sanitizeSizes(
    ((services.settings.value.ext[SIZES_EXT] ?? null) as Readonly<Record<string, Json>> | null)?.[key],
    defaults.length,
  );
  const [live, setLive] = useState<{ readonly key: string; readonly sizes: readonly number[] } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(
    () => () => {
      clearTimeout(timer.current);
    },
    [],
  );
  const raw = live?.key === key ? live.sizes : (stored ?? defaults);
  const sizes = useMemo(() => (rule === undefined ? raw : rule(raw, raw)), [raw, rule]);
  const onResize = useCallback(
    (proposed: readonly number[]): void => {
      const next = rule === undefined ? proposed : rule(proposed, sizes);
      setLive({ key, sizes: next });
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const saved = services.settings.value.ext[SIZES_EXT];
        const all =
          saved !== null && typeof saved === 'object' && !Array.isArray(saved)
            ? (saved as Readonly<Record<string, Json>>)
            : {};
        services.settings.setExt(SIZES_EXT, { ...all, [key]: [...next] });
      }, SAVE_DELAY_MS);
    },
    [key, services, rule, sizes],
  );
  return [sizes, onResize];
}

const selectMini = (snap: UiSnapshot): boolean => snap.miniPreview;
const selectDrawer = (snap: UiSnapshot): boolean => snap.drawer;
const selectPanelRequest = (snap: UiSnapshot): UiSnapshot['panelRequest'] => snap.panelRequest;
const selectFocusRequest = (snap: UiSnapshot): UiSnapshot['focusRequest'] => snap.focusRequest;

/** Opens the drawer / mini preview when a new request targets a slot that lives there. */
function useRevealRequests(ui: ShellUiService, layout: ChartLayout): void {
  const panel = useUi(ui, selectPanelRequest);
  const focus = useUi(ui, selectFocusRequest);
  // Requests already answered (a later layout change must not replay an old one).
  const handled = useRef({ panel: panel?.nonce ?? 0, focus: focus?.nonce ?? 0 });
  const { dockPlacement, inspectorPlacement, previewPlacement } = layout;
  useEffect(() => {
    const reveal = (slot: PanelSlot): void => {
      if (
        (slot === 'chart.dock' && dockPlacement === 'drawer') ||
        (slot === 'chart.inspector' && inspectorPlacement === 'drawer')
      ) {
        ui.setDrawer(true);
      }
      if (slot === 'chart.preview' && previewPlacement === 'floating') ui.setMiniPreview(true);
    };
    if (panel !== null && panel.nonce !== handled.current.panel) {
      handled.current.panel = panel.nonce;
      reveal(panel.slot);
    }
    if (focus !== null && focus.nonce !== handled.current.focus) {
      handled.current.focus = focus.nonce;
      reveal(focus.slot);
    }
  }, [panel, focus, ui, dockPlacement, inspectorPlacement, previewPlacement]);
}

/**
 * A preset that wants a dock tab (Review: Problems) selects it when it takes effect: when the preset in effect
 * changes (the UI service remembers the last one for the session), not every time the Chart workspace mounts again
 * (F2 then F3 keeps the tab the user chose).
 */
function usePresetDockTab(services: AppServices, ui: ShellUiService, preset: LayoutPreset): void {
  useEffect(() => {
    if (!ui.presetChanged(preset)) return;
    if (LAYOUT_PRESETS[preset].dockTab !== 'problems') return;
    const id = problemsPanelId(services);
    if (id !== null) ui.setActiveTab('chart.dock', id);
  }, [preset, services, ui]);
}

/** Size of an element (CSS px), followed with a ResizeObserver; null until measured. */
function useElementSize(ref: RefObject<HTMLElement | null>): { readonly width: number; readonly height: number } | null {
  const [size, setSize] = useState<{ readonly width: number; readonly height: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    const read = (): void => {
      const width = el.clientWidth;
      const height = el.clientHeight;
      setSize((prev) => (prev?.width === width && prev.height === height ? prev : { width, height }));
    };
    read();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [ref]);
  return size;
}

/** Selector of the preview panel's stage (the help hook W2-02 puts on it; everything else in the panel is chrome). */
const STAGE_SELECTOR = '[data-help-id="preview-stage"]';

/**
 * Height (CSS px) of the preview panel's chrome inside `container`'s `chart.preview` slot: the panel's height minus
 * its stage's (toolbar rows, readout, banners inside the panel). Null until the panel is mounted. Changes of less
 * than 2 px are ignored, so the layout settles at once.
 */
function usePreviewChrome(container: RefObject<HTMLElement | null>, active: boolean): number | null {
  const [chrome, setChrome] = useState<number | null>(null);
  useLayoutEffect(() => {
    const root = container.current;
    if (!active || root === null) return undefined;
    let ro: ResizeObserver | null = null;
    let observed: Element | null = null;
    const read = (): void => {
      const slot = root.querySelector<HTMLElement>('[data-slot="chart.preview"]');
      const stage = slot?.querySelector<HTMLElement>(STAGE_SELECTOR) ?? null;
      if (slot === null || stage === null) return;
      if (observed !== stage && ro !== null) {
        ro.disconnect();
        ro.observe(slot);
        ro.observe(stage);
        observed = stage;
      }
      const next = Math.max(0, slot.clientHeight - stage.clientHeight);
      setChrome((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    if (typeof ResizeObserver === 'function') ro = new ResizeObserver(read);
    read();
    // The panel mounts its stage after the first commit (lazy modules, the project opening): look again shortly.
    const timer = setInterval(read, 1000);
    return () => {
      clearInterval(timer);
      ro?.disconnect();
    };
  }, [container, active]);
  return chrome;
}

export interface WorkspaceLayoutProps {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly workspace: WorkspaceId;
}

export function WorkspaceLayout(props: WorkspaceLayoutProps) {
  const { services, ui, workspace } = props;
  const text = useText(services);
  const settings = useSettingsValue(services);
  const viewport = useLayoutViewport(services);
  const level = settings.general.level;
  const preset = effectiveLayout(settings.editing.layout, viewport);
  const mainRef = useRef<HTMLElement>(null);
  const measured = useElementSize(mainRef);
  const scale = settings.general.uiScale > 0 ? settings.general.uiScale : 1;
  const bodyHeight = measured === null || measured.height <= 0 ? undefined : measured.height / scale;
  return (
    <main
      ref={mainRef}
      className={l.workspace}
      data-workspace={workspace}
      data-layout={preset}
      data-layout-auto={preset !== settings.editing.layout ? 'true' : undefined}
      data-screen={viewport.width < BREAKPOINTS.tablet ? 'tablet' : undefined}
      aria-label={text.t(m.workspaceLabel, { workspace })}
    >
      {workspace === 'chart' ? (
        <ChartWorkspace
          services={services}
          ui={ui}
          preset={preset}
          level={level}
          viewport={viewport}
          bodyHeight={bodyHeight}
          scale={scale}
        />
      ) : workspace === 'timing' ? (
        <TimingWorkspace services={services} ui={ui} preset={preset} level={level} viewport={viewport} bodyHeight={bodyHeight} />
      ) : (
        <PublishWorkspace services={services} ui={ui} preset={preset} level={level} viewport={viewport} />
      )}
    </main>
  );
}

interface PartProps {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly preset: LayoutPreset;
  readonly level: 'guided' | 'full';
  readonly viewport: { readonly width: number; readonly height: number };
}

function ChartWorkspace(props: PartProps & { readonly bodyHeight: number | undefined; readonly scale: number }) {
  const { services, ui, preset, level, viewport, bodyHeight, scale } = props;
  const rootRef = useRef<HTMLDivElement>(null);
  const chromePx = usePreviewChrome(rootRef, true);
  const previewChrome = chromePx === null ? undefined : chromePx / scale;
  const layout = useMemo(
    () => computeChartLayout({ viewport, preset, level, bodyHeight, previewChrome }),
    [viewport, preset, level, bodyHeight, previewChrome],
  );
  const miniPreview = useUi(ui, selectMini);
  const drawer = useUi(ui, selectDrawer);
  useRevealRequests(ui, layout);
  usePresetDockTab(services, ui, layout.preset);
  const sizesKey = layoutSizesKey('chart', layout.preset, level);
  const defaults = useMemo(() => chartSplitSizes(layout), [layout]);
  // Guided: the docked preview (pane 1) never gets narrower than 35 % of the width, however the dividers move.
  const minPreview = previewMinPct(layout);
  const previewRule = useMemo<SizeRule | undefined>(
    () =>
      minPreview > 0 && defaults.length > 1 ? (next, prev) => enforcePaneMin(prev, next, 1, minPreview) : undefined,
    [minPreview, defaults.length],
  );
  const [split, onSplit] = useSplitSizes(services, `${sizesKey}:main`, defaults, previewRule);
  const [rightSplit, onRightSplit] = useSplitSizes(services, `${sizesKey}:right`, layout.rightColumn);

  const timeline = (
    <div className={l.timelineArea} data-motion-columns={layout.motionColumns}>
      <SlotHost services={services} ui={ui} slot="chart.timeline" preset={layout.preset} />
    </div>
  );
  // Motion columns below 1024 px (D§3.3, CR-W2-04a-7): a bottom drawer under the lane. The slot is shown only
  // while a module fills it (the second timeline host is W3-05's; until then the columns stay as sparklines).
  const motionDrawer = layout.motionColumns === 'drawer' && (
    <MotionDrawer services={services} ui={ui} preset={layout.preset} />
  );
  const rightColumn =
    layout.dockPlacement === 'column' ? (
      <Splitter orientation="vertical" sizes={rightSplit} onResize={onRightSplit} minSize={15}>
        <SlotHost services={services} ui={ui} slot="chart.preview" preset={layout.preset} />
        <SlotHost services={services} ui={ui} slot="chart.dock" preset={layout.preset} />
      </Splitter>
    ) : (
      <SlotHost services={services} ui={ui} slot="chart.preview" preset={layout.preset} />
    );
  const panes = [timeline];
  if (layout.previewPlacement === 'docked') panes.push(rightColumn);
  if (layout.inspectorPlacement === 'column') {
    panes.push(<SlotHost services={services} ui={ui} slot="chart.inspector" preset={layout.preset} />);
  }

  return (
    <div
      ref={rootRef}
      className={l.chart}
      data-rail-labels={layout.railLabels ? 'true' : undefined}
      data-motion-columns={layout.motionColumns}
      data-preview-chrome={chromePx === null ? undefined : Math.round(chromePx)}
      data-dock={layout.dockPlacement}
      data-preview={layout.previewPlacement}
    >
      <div className={l.rail} style={{ width: layout.rail }}>
        <SlotHost services={services} ui={ui} slot="chart.tools" preset={layout.preset} />
      </div>
      <div className={l.chartMain}>
        <div className={l.chartMainStack}>
          {panes.length === 1 ? (
            timeline
          ) : (
            <Splitter orientation="horizontal" sizes={split} onResize={onSplit} minSize={10}>
              {panes.map((pane, i) => (
                <div key={i} className={l.pane}>
                  {pane}
                </div>
              ))}
            </Splitter>
          )}
        </div>
        {motionDrawer}
      </div>
      {layout.previewPlacement === 'floating' && miniPreview && (
        <MiniPreview services={services} ui={ui} preset={layout.preset} scale={scale} />
      )}
      {(layout.dockPlacement === 'drawer' || layout.inspectorPlacement === 'drawer') && drawer && (
        <Drawer services={services} ui={ui} preset={layout.preset} layout={layout} />
      )}
    </div>
  );
}

/**
 * Motion columns' bottom drawer below 1024 px (D§3.3, CR-W2-04a-7): the `chart.motionDrawer` slot under the lane.
 * Shown only while a module fills the slot (the second timeline host is W3-05's); until then the motion columns
 * stay in the timeline as sparklines and nothing new appears.
 */
function MotionDrawer(props: { readonly services: AppServices; readonly ui: ShellUiService; readonly preset: LayoutPreset }) {
  const { services, ui, preset } = props;
  const text = useText(services);
  const panels = services.registry.panels(MOTION_DRAWER_SLOT);
  if (panels.length === 0) return null;
  return (
    <section className={l.motionDrawer} data-region="motion" aria-label={text.t(m.motionDrawerLabel)}>
      <header className={l.drawerHeader} data-chrome="">
        <span className={l.drawerTitle}>{text.t(m.motionDrawerTitle)}</span>
      </header>
      <div className={l.motionDrawerHost}>
        <SlotHost services={services} ui={ui} slot={MOTION_DRAWER_SLOT} preset={preset} />
      </div>
    </section>
  );
}

function Drawer(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly preset: LayoutPreset;
  readonly layout: ChartLayout;
}) {
  const { services, ui, preset, layout } = props;
  const text = useText(services);
  const ref = useRef<HTMLElement>(null);
  const shellFocus = useOptionalShell()?.focus ?? null;
  const handBack = useRef(shellFocus);
  useEffect(() => {
    handBack.current = shellFocus;
  });
  // The element that had focus when the drawer opened (kept across StrictMode's effect re-run).
  const [opener] = useState<Element | null>(() => (typeof document === 'undefined' ? null : document.activeElement));
  // Focus moves into the drawer when it opens (F9, the hint bar button, a panel shown there), so Escape and Tab work
  // at once; closing it gives focus back to where it was, unless the user moved it elsewhere meanwhile.
  useEffect(() => {
    const el = ref.current;
    if (el !== null && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    return () => {
      if (el?.isConnected === true) return;
      const active = document.activeElement;
      const lost = active === null || active === document.body || active === document.documentElement;
      if (!lost) return;
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus({ preventScroll: true });
      else handBack.current?.handBack();
    };
  }, [opener]);
  return (
    <aside
      ref={ref}
      className={l.drawer}
      tabIndex={-1}
      aria-label={text.t(m.drawerTitle)}
      style={{ width: Math.min(CHROME.inspector + 40, layout.width * 0.45) }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.defaultPrevented) {
          e.preventDefault();
          ui.setDrawer(false);
        }
      }}
    >
      <header className={l.drawerHeader} data-chrome="">
        <span className={l.drawerTitle}>{text.t(m.drawerTitle)}</span>
        <IconButton
          icon="X"
          size="sm"
          label={{ key: m.drawerClose }}
          onClick={() => {
            ui.setDrawer(false);
          }}
        />
      </header>
      <div className={l.drawerBody}>
        {layout.dockPlacement === 'drawer' && (
          <SlotHost services={services} ui={ui} slot="chart.dock" preset={preset} className={l.drawerDock} />
        )}
        {layout.inspectorPlacement === 'drawer' && (
          <SlotHost services={services} ui={ui} slot="chart.inspector" preset={preset} className={l.drawerInspector} />
        )}
      </div>
    </aside>
  );
}

/**
 * Where the mini preview sits: its distance (CSS px) from the window's right edge and from the top of the hint bar,
 * so it stays in the corner the user put it in when the window changes size.
 */
export interface MiniAnchor {
  readonly right: number;
  readonly bottom: number;
}

/** Position of the floating window on screen (CSS px). */
export interface MiniPos {
  readonly x: number;
  readonly y: number;
}

/** Bars around the workspace (CSS px), which the floating window never covers. */
export interface MiniBounds {
  readonly top: number;
  readonly bottom: number;
}

const DEFAULT_MINI_BOUNDS: MiniBounds = { top: CHROME.topBar, bottom: CHROME.hintBar };

/**
 * Reads the stored mini preview anchor; the Wave-2 format `{ x, y }` (top-left corner) is converted with the window
 * and window sizes it is read for.
 */
export function readMiniAnchor(
  v: Json | undefined,
  size: { readonly w: number; readonly h: number },
  win: { readonly vw: number; readonly vh: number },
  bounds: MiniBounds = DEFAULT_MINI_BOUNDS,
): MiniAnchor | null {
  if (v === null || v === undefined || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Readonly<Record<string, Json>>;
  if (typeof o.right === 'number' && typeof o.bottom === 'number' && Number.isFinite(o.right + o.bottom)) {
    return { right: o.right, bottom: o.bottom };
  }
  if (typeof o.x === 'number' && typeof o.y === 'number' && Number.isFinite(o.x + o.y)) {
    return { right: win.vw - o.x - size.w, bottom: win.vh - bounds.bottom - o.y - size.h };
  }
  return null;
}

/** Keeps a floating window of size (w, h) inside the viewport, between the bars. */
export function clampWindow(
  pos: MiniPos,
  w: number,
  h: number,
  vw: number,
  vh: number,
  bounds: MiniBounds = DEFAULT_MINI_BOUNDS,
): MiniPos {
  return {
    x: Math.min(Math.max(0, pos.x), Math.max(0, vw - w)),
    y: Math.min(Math.max(bounds.top, pos.y), Math.max(bounds.top, vh - bounds.bottom - h)),
  };
}

/** Screen position of an anchored window, kept fully visible (the header with its close button always reachable). */
export function placeMini(
  anchor: MiniAnchor,
  w: number,
  h: number,
  vw: number,
  vh: number,
  bounds: MiniBounds = DEFAULT_MINI_BOUNDS,
): MiniPos {
  return clampWindow({ x: vw - anchor.right - w, y: vh - bounds.bottom - anchor.bottom - h }, w, h, vw, vh, bounds);
}

/** The anchor of a window at `pos` (inverse of placeMini for a window inside the viewport). */
export function anchorOf(
  pos: MiniPos,
  w: number,
  h: number,
  vw: number,
  vh: number,
  bounds: MiniBounds = DEFAULT_MINI_BOUNDS,
): MiniAnchor {
  return { right: Math.max(0, vw - pos.x - w), bottom: Math.max(0, vh - bounds.bottom - pos.y - h) };
}

/**
 * Size (CSS px) of the mini preview: 420 px wide (scaled; narrower on small windows) and as tall as its 16:9 stage
 * plus the panel's chrome and the window's header, at most the room between the bars.
 */
export function miniSize(
  vw: number,
  vh: number,
  scale: number,
  chrome: number | null,
  bounds: MiniBounds = DEFAULT_MINI_BOUNDS,
): { readonly w: number; readonly h: number } {
  const margin = CHROME.miniMargin * scale;
  const w = Math.max(160, Math.min(CHROME.miniPreviewWidth * scale, vw - 2 * margin));
  const panelChrome = chrome ?? estimatePreviewChrome(w / scale) * scale;
  const wanted = Math.round((w * 9) / 16 + panelChrome + CHROME.miniHeader * scale);
  const room = Math.max(120, vh - bounds.top - bounds.bottom - 2 * margin);
  return { w: Math.round(w), h: Math.min(wanted, room) };
}

function MiniPreview(props: {
  readonly services: AppServices;
  readonly ui: ShellUiService;
  readonly preset: LayoutPreset;
  readonly scale: number;
}) {
  const { services, ui, preset, scale } = props;
  const text = useText(services);
  const win = useWindowSize();
  const vw = win.width;
  const vh = win.height;
  const bodyRef = useRef<HTMLDivElement>(null);
  const chrome = usePreviewChrome(bodyRef, true);
  const bounds = useMemo<MiniBounds>(() => ({ top: CHROME.topBar * scale, bottom: CHROME.hintBar * scale }), [scale]);
  const size = miniSize(vw, vh, scale, chrome, bounds);
  const margin = CHROME.miniMargin * scale;
  const [anchor, setAnchor] = useState<MiniAnchor>(
    () =>
      readMiniAnchor(services.settings.value.ext[MINI_EXT], size, { vw, vh }, bounds) ?? {
        right: margin,
        bottom: margin,
      },
  );
  // Derived on every render: a window resize, a layout change or a new size re-clamps it (UX-003).
  const pos = placeMini(anchor, size.w, size.h, vw, vh, bounds);
  const drag = useRef<{ id: number; dx: number; dy: number } | null>(null);
  const onPointerDown = (e: ReactPointerEvent<HTMLElement>): void => {
    if (e.button !== 0 || (e.target instanceof Element && e.target.closest('button') !== null)) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { id: e.pointerId, dx: e.clientX - pos.x, dy: e.clientY - pos.y };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>): void => {
    const d = drag.current;
    if (d?.id !== e.pointerId) return;
    const next = clampWindow({ x: e.clientX - d.dx, y: e.clientY - d.dy }, size.w, size.h, vw, vh, bounds);
    setAnchor(anchorOf(next, size.w, size.h, vw, vh, bounds));
  };
  const onPointerUp = (e: ReactPointerEvent<HTMLElement>): void => {
    if (drag.current?.id !== e.pointerId) return;
    drag.current = null;
    // Store where it is shown (already clamped), anchored to the bottom-right corner.
    const shown = anchorOf(pos, size.w, size.h, vw, vh, bounds);
    services.settings.setExt(MINI_EXT, { right: Math.round(shown.right), bottom: Math.round(shown.bottom) });
  };
  return (
    <div
      className={l.miniPreview}
      role="region"
      aria-label={text.t(m.miniPreviewTitle)}
      data-mini-preview=""
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
    >
      <header
        className={l.miniHeader}
        data-chrome=""
        title={text.t(m.miniPreviewMove)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <span className={l.drawerTitle}>{text.t(m.miniPreviewTitle)}</span>
        <IconButton
          icon="X"
          size="sm"
          label={{ key: m.miniPreviewHide }}
          onClick={() => {
            ui.setMiniPreview(false);
          }}
        />
      </header>
      <div className={l.miniBody} ref={bodyRef}>
        <SlotHost services={services} ui={ui} slot="chart.preview" preset={preset} />
      </div>
    </div>
  );
}

function TimingWorkspace(props: PartProps & { readonly bodyHeight?: number | undefined }) {
  const { services, ui, preset, level, viewport, bodyHeight } = props;
  const t = computeTimingLayout(viewport, bodyHeight);
  const total = Math.max(1, viewport.width);
  const key = layoutSizesKey('timing', preset, level);
  const [cols, onCols] = useSplitSizes(services, `${key}:cols`, [(t.left / total) * 100, (t.main / total) * 100]);
  const [rows, onRows] = useSplitSizes(services, `${key}:rows`, t.rows);
  return (
    <Splitter orientation="horizontal" sizes={cols} onResize={onCols} minSize={12}>
      <SlotHost services={services} ui={ui} slot="timing.left" preset={preset} />
      <Splitter orientation="vertical" sizes={rows} onResize={onRows} minSize={12}>
        <SlotHost services={services} ui={ui} slot="timing.main" preset={preset} />
        <SlotHost services={services} ui={ui} slot="timing.bottom" preset={preset} />
      </Splitter>
    </Splitter>
  );
}

function PublishWorkspace(props: PartProps) {
  const { services, ui, preset, level, viewport } = props;
  const key = layoutSizesKey('publish', preset, level);
  const [cols, onCols] = useSplitSizes(services, `${key}:cols`, computePublishLayout(viewport));
  return (
    <Splitter orientation="horizontal" sizes={cols} onResize={onCols} minSize={20}>
      <SlotHost services={services} ui={ui} slot="publish.left" preset={preset} />
      <SlotHost services={services} ui={ui} slot="publish.right" preset={preset} />
    </Splitter>
  );
}
